/**
 * <related-products> — the always-on related rail under a product
 * (sections/related-products.liquid). Curated metafield cards arrive
 * server-rendered; this component adds the personal and fallback layers:
 *
 * - "Fits your trailer": when a reg-nr lookup is saved (assets/
 *   trailer-context.js) it asks window.ValerydCards.cards() for the trailer's
 *   fitting SKUs — minus this product's own — capped at 8, with a view-all
 *   link to the full reg-nr results. The cards are the server-rendered ones. Re-renders live when
 *   the shopper saves/forgets a trailer ('trailer:changed').
 * - Native recommendations: fetched at most once through the Section
 *   Rendering API (the section renders its own recommendation grid when
 *   `recommendations.performed`) and only when the rail would otherwise be
 *   empty, so the section never renders blank.
 *
 * The section starts `hidden` unless curated cards rendered; this component
 * unhides it once any grid holds a card. Without JS the curated layer alone
 * shows (or the whole section stays hidden) — nothing breaks.
 */
if (!customElements.get('related-products')) {
  customElements.define(
    'related-products',
    class RelatedProducts extends HTMLElement {
      connectedCallback() {
        this.trailerRail = this.querySelector('[data-trailer-rail]');
        this.recsRail = this.querySelector('[data-recs-rail]');
        this.refreshId = 0;
        this.onChange = () => this.refresh();
        document.addEventListener('trailer:changed', this.onChange);
        this.refresh();
      }

      disconnectedCallback() {
        this.refreshId += 1;
        document.removeEventListener('trailer:changed', this.onChange);
      }

      async refresh() {
        const refreshId = ++this.refreshId;
        await this.renderTrailerRail(refreshId);
        if (refreshId !== this.refreshId) return;
        if (!this.hasCards()) await this.loadRecommendations();
        this.hidden = !this.hasCards();
      }

      hasCards() {
        return !!this.querySelector('.related__grid li');
      }

      // This product's own SKUs — never listed as related to itself.
      ownSkus() {
        return (this.dataset.skus || '')
          .split(',')
          .map((sku) => sku.trim().toUpperCase())
          .filter(Boolean);
      }

      async renderTrailerRail(refreshId) {
        const store = window.ValerydTrailer;
        const rail = this.trailerRail;
        if (!store || !rail) return;

        const empty = () => {
          rail.hidden = true;
          rail.replaceChildren();
        };

        const saved = store.read();
        if (!saved) return empty();

        const own = this.ownSkus();
        const skus = Array.from(new Set(store.skus(saved))).filter((sku) => own.indexOf(sku) === -1);
        const shown = skus.slice(0, 8);
        if (!shown.length) return empty();

        const rendered = await window.ValerydCards.cards(shown);
        if (refreshId !== this.refreshId) return;

        const grid = document.createElement('ul');
        grid.className = 'related__grid';
        grid.setAttribute('role', 'list');
        shown.forEach((sku) => {
          const card = rendered.bySku.get(sku);
          if (card) grid.appendChild(card);
        });
        if (!grid.children.length) return empty(); // nothing resolved to a live product

        const heading = document.createElement('h3');
        heading.className = 'related__group';
        heading.textContent = (this.dataset.fitsHeading || '%REGNR%').replace('%REGNR%', saved.regnr);

        const viewAll = document.createElement('a');
        viewAll.className = 'related__view-all';
        viewAll.href = (this.dataset.searchUrl || ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'search') + '?q=' + encodeURIComponent(saved.regnr);
        viewAll.textContent = (this.dataset.viewAll || '').replace('%REGNR%', saved.regnr);

        rail.replaceChildren(heading, grid, viewAll);
        rail.hidden = false;
      }

      async loadRecommendations() {
        if (this.recsLoaded || !this.recsRail || !this.dataset.recsUrl || !this.dataset.sectionId) return;
        this.recsLoaded = true;
        try {
          const url =
            this.dataset.recsUrl +
            '?section_id=' +
            encodeURIComponent(this.dataset.sectionId) +
            '&product_id=' +
            encodeURIComponent(this.dataset.productId) +
            '&limit=4&intent=related';
          const res = await fetch(url);
          if (!res.ok) return;
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          // Same-origin HTML rendered by Shopify from this very section — the
          // grid is adopted wholesale so cards match the server-rendered ones.
          const grid = doc.querySelector('[data-recs-grid]');
          if (grid && grid.querySelector('li')) {
            this.recsRail.replaceChildren(grid);
            this.recsRail.hidden = false;
          }
        } catch (error) {
          /* fallback layer only — the rail simply stays empty */
        }
      }
    }
  );
}
